import React, { useEffect, useMemo, useState } from "react";

import {
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestLabelHierarchyNodeMappingPackage,
	FrontendCompanyTestLabels,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
	TestHierarchyTreeNode,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";

import { Divider } from "@mui/material";
import { TestHierarchyNodeType } from "node-library/dist/constants";
import { CustomCheckbox } from "../../../../common-components/inputs";
import { getEnabledHierarchyNodeTree } from "../../../../helpers/testHierarchyHelper";
import PrintLabelSampleCard from "./printLabelSampleCard";
import { LabelInputState } from "../../../../helpers/testLabelHelper";
import CreateOrUpdateSpecificationReadingDrawer from "../../../../common-components/drawers/createOrUpdateSpecificationReadingDrawer";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;

	orderTestCategory: FrontendCompanyTestCategory;

	sampleInstances: Array<FrontendSampleInstance>;

	hierarchyNodeParentMap: Map<string, FrontendCompanyTestHierarchyNode[]>;
	labelTestGroupNodeIdSet: Set<string>;
	filterByTestGroupIds: Array<string>;
	setFilterByTestGroupIds: React.Dispatch<React.SetStateAction<Array<string>>>;
	filteredSampleData: Array<{
		sampleInstance: FrontendSampleInstance;
		instanceHierarchyTree: TestHierarchyTreeNode;
		matchingLabelAndPackages: Array<{
			label: FrontendCompanyTestLabels;
			matchingPackages: Array<FrontendCompanyTestLabelHierarchyNodeMappingPackage>;
		}>;
	}>;
	selectedInstances: Set<string>;
	setSelectedInstances: React.Dispatch<React.SetStateAction<Set<string>>>;

	labelInputMap: LabelInputState;
	handleLabelInputChange: (
		sampleInstanceId: string,
		labelId: string,
		secondaryLabelId: string,

		packageId: string,
		value: string,
	) => void;

	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function PrintLabelSamplePreviewTable(props: Props) {
	const { t: translation } = useTranslation();

	const {
		company,
		setCompany,
		user,
		sampleInstances,
		hierarchyNodeParentMap,
		orderTestCategory,
		labelTestGroupNodeIdSet,
		filterByTestGroupIds,
		setFilterByTestGroupIds,
		selectedInstances,
		setSelectedInstances,
		filteredSampleData,
		labelInputMap,
		handleLabelInputChange,
		specDirectories,
		updateSpecification,
	} = props;

	const [selectAllCheckboxChecked, setSelectAllCheckboxChecked] =
		useState<boolean>(true);

	const categoryTestGroupNodes: Array<FrontendCompanyTestHierarchyNode> =
		orderTestCategory.hierarchyNodes.filter((node) => {
			return node.nodeType === TestHierarchyNodeType.TEST_GROUP;
		});

	const [readingDrawerOpen, setReadingDrawerOpen] = useState<boolean>(false);
	const [selectedSpecification, setSelectedSpecification] = useState<
		FrontendSampleSpecification | undefined
	>();

	function openSpecReadingDrawer(spec: FrontendSampleSpecification) {
		setSelectedSpecification(spec);
		setReadingDrawerOpen(true);
	}

	useEffect(() => {
		const allVisibleIds = new Set(
			filteredSampleData.map((item) => item.sampleInstance.sampleInstanceId),
		);
		let allSelected = true;
		for (const id of Array.from(allVisibleIds)) {
			if (!selectedInstances.has(id)) {
				allSelected = false;
				break;
			}
		}
		setSelectAllCheckboxChecked(allSelected);
	}, [filteredSampleData, selectedInstances]);

	const headerRow: React.ReactNode = (
		<div className="print-label-list-row">
			{
				<CustomCheckbox
					className={``}
					label={``}
					checked={selectAllCheckboxChecked}
					setChecked={() => {
						const newState: boolean = !selectAllCheckboxChecked;
						setSelectAllCheckboxChecked(newState);
						if (newState) {
							const all = new Set(
								filteredSampleData.map(
									(o) => o.sampleInstance.sampleInstanceId,
								),
							);
							setSelectedInstances(all);
						} else {
							setSelectedInstances(new Set());
						}
					}}
				/>
			}
			<div className={`print-label-info-list-row sample-preview-header-row`}>
				<div className="sample-preview-header-column sample-preview-number-column">
					{translation("SampleId")}
					<Divider orientation="vertical" flexItem />
				</div>
				<div className="sample-preview-header-column">
					{translation("SampleInformation")}
					<Divider orientation="vertical" flexItem />
				</div>
				<div className="sample-preview-header-column ">
					{translation("TestsAndTurnaroundTime")}
					<Divider orientation="vertical" flexItem />
				</div>
				<div
					className="sample-preview-header-column"
					style={{ justifyContent: "space-around" }}
				>
					{translation("Show")}
					<div className="placeholder"></div>
				</div>
			</div>
		</div>
	);

	const toggleCheckboxOption = (testGroupId: string) => {
		setFilterByTestGroupIds((prev) => {
			// Check if the batchId already exists
			const exists = prev.includes(testGroupId);

			// Create a new array with batchId added or removed
			const updatedTestGroupIds = exists
				? prev.filter((id) => id !== testGroupId)
				: [...prev, testGroupId];

			// Return updated state
			return updatedTestGroupIds;
		});
	};

	const testGroupFilterFields: React.ReactNode =
		categoryTestGroupNodes.length > 1 ? (
			<div className="test-group-option-filter-row typography-body-02">
				{`${translation("Filterby")}:`}
				{categoryTestGroupNodes
					.filter((node) => {
						return labelTestGroupNodeIdSet.has(node.nodeId);
					})
					.map((node) => {
						const selected: boolean = filterByTestGroupIds.includes(
							node.nodeId,
						);
						return (
							<div key={node.nodeId}>
								<CustomCheckbox
									className={`test-group-option-filter-checkbox ${
										selected && "test-group-option-filter-checkbox-checked"
									}`}
									label={node.nodeName}
									checked={selected}
									setChecked={() => toggleCheckboxOption(node.nodeId)}
								/>
							</div>
						);
					})}
			</div>
		) : null;

	const sampleRows: Array<React.ReactNode> = filteredSampleData
		? filteredSampleData.map(
				(
					{ sampleInstance, instanceHierarchyTree, matchingLabelAndPackages },
					index: number,
				) => {
					if (
						instanceHierarchyTree.children.length == 0 &&
						!sampleInstance.sampleTestInfo?.additionalTest
					) {
						return null;
					}

					if (
						!matchingLabelAndPackages ||
						matchingLabelAndPackages.length == 0
					) {
						return null;
					}

					return (
						<PrintLabelSampleCard
							company={company}
							hierarchyNodeParentMap={hierarchyNodeParentMap}
							instanceHierarchyTree={instanceHierarchyTree}
							matchingLabelAndPackages={matchingLabelAndPackages}
							orderTestCategory={orderTestCategory}
							key={index}
							sampleInstance={sampleInstance}
							selected={selectedInstances.has(sampleInstance.sampleInstanceId)}
							setSelected={(selected: boolean) => {
								const updated = new Set(selectedInstances);
								if (selected) {
									updated.add(sampleInstance.sampleInstanceId);
									if (
										filteredSampleData.every((data) =>
											selectedInstances.has(
												data.sampleInstance.sampleInstanceId,
											),
										)
									) {
										setSelectAllCheckboxChecked(true);
									}
								} else {
									updated.delete(sampleInstance.sampleInstanceId);
									setSelectAllCheckboxChecked(false);
								}
								setSelectedInstances(updated);
							}}
							labelInputMap={labelInputMap}
							handleLabelInputChange={handleLabelInputChange}
							openSpecReadingDrawer={() => {
								if (sampleInstance.specification) {
									openSpecReadingDrawer(sampleInstance.specification);
								}
							}}
						/>
					);
				},
			)
		: [];

	return (
		<>
			<div className={`sample-preview-table-container`}>
				{testGroupFilterFields}

				{headerRow}

				{sampleRows}
				{readingDrawerOpen && selectedSpecification && (
					<CreateOrUpdateSpecificationReadingDrawer
						company={company}
						setCompany={setCompany}
						testCategory={orderTestCategory}
						// Derive matrix from the sample associated with the spec
						selectedMatrix={
							orderTestCategory.sampleMatrices.find(
								(m) => m.matrixId === selectedSpecification.matrixId,
							) as FrontendCompanySampleMatrix
						}
						selectedSpecFile={selectedSpecification}
						user={user}
						open={readingDrawerOpen}
						setOpen={setReadingDrawerOpen}
						specDirectories={specDirectories}
						updateSpecification={updateSpecification}
					/>
				)}
			</div>
		</>
	);
}
